import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  TvButton,
  TvCard,
  TvCardBody,
  TvCol,
  TvLabel,
  TvRow,
  TvInput,
  TvDiv,
  TvImg,
} from "@trustvid/design-system";

import TemplateCard from "../../Templates/components/TemplateCard";

const getProductImageUrl = (product) =>
  product?.image_url ||
  product?.image ||
  product?.thumbnail_url ||
  product?.featuredImage?.url ||
  product?.image?.src ||
  "";

const hasTemplatePreview = (template) => Boolean(String(template?.thumbnail_url || "").trim());

const getRatioCardSize = (ratio) => {
  if (ratio === "9:16") {
    return { width: "92px", height: "128px" };
  }
  if (ratio === "1:1") {
    return { width: "100px", height: "100px" };
  }
  return { width: "148px", height: "88px" };
};

const horizontalScrollerStyle = {
  display: "flex",
  flexWrap: "nowrap",
  gap: "10px",
  overflowX: "auto",
  overflowY: "hidden",
  paddingBottom: "6px"
};

const ShopifyCreateOptionsPanel = ({
  ratioOptions,
  selectedAspectRatio,
  onSelectAspectRatio,
  shopifyProducts,
  selectedShopifyProductHandle,
  onSelectShopifyProductHandle,
  shopifyTemplates,
  selectedShopifyTemplateId,
  onSelectShopifyTemplateId,
  shopifyCreateMode,
  onShopifyCreateModeChange,
}) => {
  const [productSearchTerm, setProductSearchTerm] = useState("");

  const filteredShopifyProducts = useMemo(() => {
    const normalizedSearchTerm = productSearchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) {
      return shopifyProducts;
    }

    return shopifyProducts.filter((product) => {
      const productName = String(product?.title || product?.handle || "").toLowerCase();
      return productName.includes(normalizedSearchTerm);
    });
  }, [productSearchTerm, shopifyProducts]);

  return (
    <TvRow className="g-3">
      <TvCol xs={12}>
        <TvDiv className="d-flex flex-wrap gap-2">
          <TvButton
            size="sm"
            color={shopifyCreateMode === "template" ? "primary" : "light"}
            className={shopifyCreateMode === "template" ? "" : "border"}
            onClick={() => onShopifyCreateModeChange("template")}
          >
            Use Template
          </TvButton>
          <TvButton
            size="sm"
            color={shopifyCreateMode === "blank" ? "primary" : "light"}
            className={shopifyCreateMode === "blank" ? "" : "border"}
            onClick={() => onShopifyCreateModeChange("blank")}
          >
            Blank Draft
          </TvButton>
          <TvButton
            size="sm"
            color={shopifyCreateMode === "ai" ? "primary" : "light"}
            className={shopifyCreateMode === "ai" ? "" : "border"}
            onClick={() => onShopifyCreateModeChange("ai")}
          >
            AI (Template + Product)
          </TvButton>
        </TvDiv>
      </TvCol>

      {shopifyCreateMode === "blank" && (
        <TvCol xs={12}>
          <TvLabel className="fw-semibold mb-2 d-block">Aspect Ratio</TvLabel>
          <TvRow className="g-2">
            {ratioOptions.map((option) => {
              const isSelected = selectedAspectRatio === option.ratio;
              const dynamicStyle = getRatioCardSize(option.ratio);
              return (
                <TvCol key={option.ratio} xs="auto">
                  <TvCard
                    className={`h-100 border ${isSelected ? "border-primary shadow-sm" : "border-light"}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectAspectRatio(option.ratio)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectAspectRatio(option.ratio);
                      }
                    }}
                    data-testid={`entry-shopify-ratio-${option.ratio.replace(":", "-")}`}
                  >
                    <TvCardBody className="p-2">
                      <TvDiv
                        className="rounded mb-1"
                        style={{
                          ...dynamicStyle,
                          background: "linear-gradient(135deg,#e9d5ff,#dbeafe)",
                          border: "1px solid #d1d5db"
                        }}
                      />
                      <TvDiv className="small fw-semibold">{option.label}</TvDiv>
                      <TvDiv className="small text-muted">{option.ratio}</TvDiv>
                    </TvCardBody>
                  </TvCard>
                </TvCol>
              );
            })}
          </TvRow>
        </TvCol>
      )}

      {shopifyCreateMode === "template" && (
        <TvCol xs={12}>
          <TvLabel className="fw-semibold mb-2 d-block">Shopify Template</TvLabel>
          <TvDiv style={horizontalScrollerStyle}>
            {shopifyTemplates.filter(hasTemplatePreview).map((template) => {
              const isSelected = String(selectedShopifyTemplateId || "") === String(template.id);
              return (
                <TvDiv
                  key={template.id}
                  style={{
                    flex: "0 0 230px",
                    minWidth: "230px",
                    borderRadius: "12px",
                    border: isSelected ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                    padding: "4px",
                    background: "#fff"
                  }}
                  data-testid={`entry-shopify-template-only-${template.id}`}
                >
                  <TemplateCard
                    template={template}
                    onUseTemplate={() => onSelectShopifyTemplateId(String(template.id))}
                    onExportTemplate={() => {}}
                    onDeleteTemplate={() => {}}
                    showActions={false}
                    compact
                    useTemplateDisabled={false}
                  />
                </TvDiv>
              );
            })}
          </TvDiv>
        </TvCol>
      )}

      {shopifyCreateMode === "ai" && (
        <>
          <TvCol xs={12}>
            <TvLabel className="fw-semibold mb-2 d-block">Shopify Product</TvLabel>
            <TvInput
              type="text"
              placeholder="Search product by name"
              value={productSearchTerm}
              onChange={(event) => setProductSearchTerm(event.target.value)}
              className="mb-2"
              data-testid="entry-shopify-product-search"
            />
            <TvDiv style={horizontalScrollerStyle}>
              {filteredShopifyProducts.map((product) => {
                const productHandle = product.handle || product.id;
                const isSelected = selectedShopifyProductHandle === productHandle;
                const imageUrl = getProductImageUrl(product);
                return (
                  <TvCard
                    key={productHandle}
                    className={`h-100 border ${isSelected ? "border-primary shadow-sm" : "border-light"}`}
                    role="button"
                    tabIndex={0}
                    style={{ flex: "0 0 220px", minWidth: "220px" }}
                    onClick={() => onSelectShopifyProductHandle(productHandle)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectShopifyProductHandle(productHandle);
                      }
                    }}
                    data-testid={`entry-shopify-product-${productHandle}`}
                  >
                    <TvCardBody className="p-2">
                      {imageUrl ? (
                        <TvDiv className="ratio ratio-1x1 mb-2 overflow-hidden rounded bg-light">
                          <TvImg
                            src={imageUrl}
                            alt={product.title || productHandle}
                            className="w-100 h-100 object-fit-cover"
                          />
                        </TvDiv>
                      ) : (
                        <TvDiv className="ratio ratio-1x1 mb-2 rounded bg-light d-flex align-items-center justify-content-center text-muted small">
                          No image
                        </TvDiv>
                      )}
                      <TvDiv className="small fw-semibold text-truncate" title={product.title || productHandle}>
                        {product.title || productHandle}
                      </TvDiv>
                    </TvCardBody>
                  </TvCard>
                );
              })}
            </TvDiv>
            {filteredShopifyProducts.length === 0 ? (
              <TvDiv className="small text-muted">No products found for this search.</TvDiv>
            ) : null}
          </TvCol>
          <TvCol xs={12}>
            <TvLabel className="fw-semibold mb-2 d-block">Template</TvLabel>
            <TvDiv style={horizontalScrollerStyle}>
              {shopifyTemplates.filter(hasTemplatePreview).map((template) => {
                const isSelected = String(selectedShopifyTemplateId || "") === String(template.id);
                return (
                  <TvDiv
                    key={template.id}
                    style={{
                      flex: "0 0 230px",
                      minWidth: "230px",
                      borderRadius: "12px",
                      border: isSelected ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                      padding: "4px",
                      background: "#fff"
                    }}
                    data-testid={`entry-shopify-template-${template.id}`}
                  >
                    <TemplateCard
                      template={template}
                      onUseTemplate={() => onSelectShopifyTemplateId(String(template.id))}
                      onExportTemplate={() => {}}
                      onDeleteTemplate={() => {}}
                      showActions={false}
                      compact
                      useTemplateDisabled={false}
                    />
                  </TvDiv>
                );
              })}
            </TvDiv>
          </TvCol>
          <TvCol xs={12}>
            <TvDiv className="small text-muted">
              Choose product and template cards, then create AI draft. Name/description can be edited later.
            </TvDiv>
          </TvCol>
        </>
      )}
    </TvRow>
  );
};

ShopifyCreateOptionsPanel.propTypes = {
  ratioOptions: PropTypes.arrayOf(
    PropTypes.shape({
      ratio: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedAspectRatio: PropTypes.string,
  onSelectAspectRatio: PropTypes.func.isRequired,
  shopifyProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShopifyProductHandle: PropTypes.string,
  onSelectShopifyProductHandle: PropTypes.func.isRequired,
  shopifyTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShopifyTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectShopifyTemplateId: PropTypes.func.isRequired,
  shopifyCreateMode: PropTypes.oneOf(["blank", "template", "ai"]).isRequired,
  onShopifyCreateModeChange: PropTypes.func.isRequired,
};

ShopifyCreateOptionsPanel.defaultProps = {
  selectedAspectRatio: "",
  selectedShopifyProductHandle: "",
  selectedShopifyTemplateId: "",
};

export default ShopifyCreateOptionsPanel;
